import { Stack, Typography } from '@mui/material';
import { isValid } from 'date-fns';
import { useAtom, useAtomValue } from 'jotai';
import { useCallback, useMemo, useState } from 'react';
import { ControllerRenderProps } from 'react-hook-form';
import { FieldFactory } from '../../../../components/Form/FieldFactory';
import { ICaptableDataModel } from '../../../../data-models/captable2.data-model';
import { RendererType } from '../../../../data-models/field-definition';
import { PermissionKey } from '../../../../services/PermissionAndRolesKeys';
import { PermissionService } from '../../../../services/PermissionService';
import { captableAsOfDateStateJ } from '../../../../services/state/CapTableDataStateJ';
import { dateFromTimelessString, dateToPlainDateString } from '../../../../util/date-utilts';
import { FMT } from '../../../../util/formatter-service';
import { createFormField } from '../../../../view-models/form.view-model';
import { useCaptableActions } from '../CapTableActions';
import { selectedCompanyIdProfileAtom } from '../../state/UIStateJ';

// --- Legacy version (flag off): updates captable directly ---

export function CaptableAsOfDateLegacy({ captable }: { captable: ICaptableDataModel | null }) {
  const { dateFormField, formProps } = useCaptableAsOfDateFieldLegacy(captable);

  if (!captable?.date && dateFormField.disabled) {
    return null;
  }

  if (dateFormField.disabled) {
    return (
      <Typography variant='body2' color='text.secondary'>
        {`${dateFormField.description!}: ${FMT.format('date', captable?.date)}`}
      </Typography>
    );
  }

  return (
    <Stack direction={'row'} gap={'0.5rem'} alignItems={'center'}>
      <Typography variant='body2' color='text.secondary'>
        {dateFormField.description!}
      </Typography>
      <FieldFactory formField={dateFormField} formProps={formProps} />
    </Stack>
  );
}

export function useCaptableAsOfDateFieldLegacy(captable: ICaptableDataModel | null) {
  const { debouncedUpdateCaptableHandler } = useCaptableActions();
  const [date, setDate] = useState<Date | null>(() =>
    captable?.date ? dateFromTimelessString(captable.date) : null
  );

  const dateFormField = useMemo(
    () =>
      createFormField({
        key: 'asOfDate',
        description: 'As Of Date',
        renderer: RendererType.date,
        disabled: !PermissionService.get().hasPermission(PermissionKey.canWriteCaptable),
      }),
    []
  );

  const onChange = useCallback(
    (val: Date) => {
      if (dateFormField.disabled || !isValid(val)) {
        return;
      }
      setDate(val);
      const asOfDate = dateToPlainDateString(val);
      debouncedUpdateCaptableHandler({ ...captable, date: asOfDate }, { showLoadingBar: true });
    },
    [captable, dateFormField.disabled, debouncedUpdateCaptableHandler]
  );

  const formProps = useMemo(
    () =>
      ({
        onChange,
        value: date,
      }) as ControllerRenderProps,
    [date, onChange]
  );

  return {
    dateFormField,
    formProps,
  };
}

// --- New version (flag on): updates shared asOfDate state to trigger refetch ---

export function CaptableAsOfDate() {
  const { dateFormField, formProps } = useCaptableAsOfDateField();

  return (
    <Stack direction={'row'} gap={'0.5rem'} alignItems={'center'}>
      <Typography variant='body2' color='text.secondary'>
        {dateFormField.description!}
      </Typography>
      <FieldFactory formField={dateFormField} formProps={formProps} />
    </Stack>
  );
}

export function useCaptableAsOfDateField() {
  const companyId = useAtomValue(selectedCompanyIdProfileAtom);
  const [asOfDate, setAsOfDate] = useAtom(captableAsOfDateStateJ(companyId));

  const dateFormField = useMemo(
    () =>
      createFormField({
        key: 'asOfDate',
        description: 'As Of Date',
        renderer: RendererType.date,
      }),
    []
  );

  const onChange = useCallback(
    (val: Date) => {
      if (!isValid(val)) {
        return;
      }
      setAsOfDate(dateToPlainDateString(val));
    },
    [setAsOfDate]
  );

  const dateValue = useMemo(() => {
    return asOfDate ? dateFromTimelessString(asOfDate) : null;
  }, [asOfDate]);

  const formProps = useMemo(
    () =>
      ({
        onChange,
        value: dateValue,
      }) as ControllerRenderProps,
    [dateValue, onChange]
  );

  return {
    dateFormField,
    formProps,
  };
}
